import React, {useState} from 'react';
import {Box, Checkbox, Menu, MenuItem, Typography} from '@mui/material';

/**
 * Multi-select menu field component for dropdown selections with multiple values
 * Manages its own anchor state internally
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {string} props.field - Field name
 * @param {Array<Object>} props.value - Current selected values (array of {value, label} objects)
 * @param {Array} props.options - Array of {value, label} options
 * @param {Function} props.onSelect - Handler for selecting menu items (receives field, array of objects)
 */
export default function MultiSelectMenuField({
                                                 label,
                                                 field,
                                                 value = [],
                                                 options,
                                                 onSelect
                                             }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const plainValues = value && typeof value.toJSON === 'function' ? value.toJSON() : value;

    const handleOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleToggle = (option) => {
        if (!option.value) return; // Skip empty value option

        const currentValues = Array.isArray(plainValues) ? plainValues : [];

        // Check if option is already selected by comparing value property
        const isSelected = currentValues.some(v =>
            (typeof v === 'object' ? v.value : v) === option.value
        );

        const newValues = isSelected
            ? currentValues.filter(v =>
                (typeof v === 'object' ? v.value : v) !== option.value
            )
            : [...currentValues, {value: option.value, label: option.label}];

        onSelect(field, newValues);
    };

    const displayValue = () => {
        const currentValues = Array.isArray(plainValues) ? plainValues : [];
        if (currentValues.length === 0) {
            return `Select ${label.toLowerCase()}`;
        }

        // Show all selected values as comma-separated list
        const selectedLabels = currentValues
            .map(val => {
                const plainVal = val && typeof val.toJSON === 'function' ? val.toJSON() : val;
                // Handle both object and legacy string format
                if (typeof plainVal === 'object' && plainVal !== null) {
                    return plainVal.value || plainVal.label; // Prefer value abbreviation
                }
                // Legacy format: val is a string, find the option
                const option = options.find(opt => opt.value === plainVal);
                return option ? option.value : plainVal;
            })
            .filter(Boolean);

        if (selectedLabels.length === 0) {
            return `Select ${label.toLowerCase()}`;
        }

        // Display all selected values without truncation
        return selectedLabels.join(', ');
    };
    const isEmpty = !Array.isArray(plainValues) || plainValues.length === 0;

    return (
        <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1.5}}>
            <Typography variant="body2" color="text.secondary" sx={{minWidth: '150px', pt: 0.5, flexShrink: 0}}>
                {label}:
            </Typography>
            <Typography
                variant="body1"
                onClick={handleOpen}
                sx={{
                    color: isEmpty ? 'primary.main' : 'text.primary',
                    cursor: 'pointer',
                    lineHeight: 1.5,
                    flex: 1,
                    minWidth: 0,
                }}
            >
                {displayValue()}
            </Typography>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        sx: {
                            maxHeight: 400,
                            width: 440,
                        }
                    }
                }}
            >
                {options.map((option) => {
                    // Skip the "Select..." placeholder option for multi-select
                    if (!option.value) return null;

                    // Check if this option is selected
                    const isSelected = Array.isArray(plainValues) && plainValues.some(v => {
                            const plainVal = v && typeof v.toJSON === 'function' ? v.toJSON() : v;
                            return (typeof plainVal === 'object' ? plainVal.value : plainVal) === option.value;
                        }
                    );

                    return (
                        <MenuItem
                            key={option.value}
                            onClick={() => handleToggle(option)}
                            dense
                            sx={{whiteSpace: 'normal', alignItems: 'flex-start'}}
                        >
                            <Checkbox
                                checked={isSelected}
                                size="small"
                                sx={{mr: 1, mt: '-2px', flexShrink: 0}}
                            />
                            {option.label}
                        </MenuItem>
                    );
                })}
            </Menu>
        </Box>
    );
}

